<template>
	<BaseModal :title="t('title')" v-model="model" noPadding>
		<div class="px-5">
			<div class="text-label-secondary -mt-2 px-3 text-center text-sm">
				{{ t('desc') }}
			</div>
			<BaseCard
				class="mt-5 flex items-center gap-4 py-5 pl-5"
				alt
				:class="{
					'pr-5 *:opacity-30': user.isChatMember,
					'pr-2': !user.isChatMember
				}"
				@click="openLink(joinLink)"
				v-hover="{ active: 'scale-95', impact: 'soft' }"
			>
				<LazyImage
					:src="chatMember"
					:alt="t('chatMember')"
					class="h-11 w-11 shrink-0"
				/>
				<div>
					<div class="text-label-primary text-sm font-medium">
						{{ t('chatMember') }}
					</div>
					<div class="text-label-secondary mt-1 text-xs font-medium uppercase">
						{{ user.isChatMember ? t('complete') : t('incomplete') }}
					</div>
				</div>
				<div
					class="text-label-tertiary ml-auto inline-flex items-center gap-0.5 text-xs font-medium uppercase"
					v-if="!user.isChatMember"
				>
					<div>{{ t('join') }}</div>
					<CaretRightIcon class="h-6 w-6" />
				</div>
				<CircleCheckIcon v-else class="text-label-primary ml-auto h-5 w-5" />
			</BaseCard>
			<BaseCard
				class="mt-1.5 flex items-center gap-4 py-5 pl-5"
				alt
				:class="{
					'pr-5 *:opacity-30': user.isSubscribed,
					'pr-2': !user.isSubscribed
				}"
				@click="openLink(subscribeLink)"
				v-hover="{ active: 'scale-95', impact: 'soft' }"
			>
				<LazyImage
					:src="channelFollower"
					:alt="t('follower')"
					class="h-11 w-11 shrink-0"
				/>
				<div>
					<div class="text-label-primary text-sm font-medium">
						{{ t('follower') }}
					</div>
					<div class="text-label-secondary mt-1 text-xs font-medium uppercase">
						{{ user.isSubscribed ? t('complete') : t('incomplete') }}
					</div>
				</div>
				<div
					class="text-label-tertiary ml-auto inline-flex items-center gap-0.5 text-xs font-medium uppercase"
					v-if="!user.isSubscribed"
				>
					<div>{{ t('follow') }}</div>
					<CaretRightIcon class="h-6 w-6" />
				</div>
				<CircleCheckIcon v-else class="text-label-primary ml-auto h-5 w-5" />
			</BaseCard>
		</div>
		<BottomActionsModal class="mt-5 pt-4">
			<BaseButton
				size="lg"
				color="primary"
				class="w-full"
				@click="retry()"
				:loading="loading"
			>
				{{ t('check') }}
			</BaseButton>
		</BottomActionsModal>
	</BaseModal>
</template>
<script setup lang="ts">
import { ref } from 'vue'
import BaseModal from './BaseModal.vue'
import BaseButton from '../BaseButton.vue'
import BaseCard from '../BaseCard.vue'
import { useI18n } from 'vue-i18n'
import chatMember from '@/assets/illustrations/chat-member.png'
import channelFollower from '@/assets/illustrations/channel-follower.png'
import { useUser } from '@/stores/user'
import CircleCheckIcon from '@/assets/icons/circle-check.svg'
import BottomActionsModal from '../BottomActionsModal.vue'
import CaretRightIcon from '@/assets/icons/caret-right.svg'
import { useToast } from '@/stores/toast'
import { WebApp } from '@grammyjs/web-app'
import LazyImage from '../LazyImage.vue'
import { sleep } from '@/utils/sleep'

const { t } = useI18n({
	messages: {
		en: {
			title: 'one more step...',
			desc: 'Please, make sure your account meets the requirements to claim rain',
			chatMember: 'Chat member',
			complete: 'complete',
			incomplete: 'incomplete',
			join: 'join',
			follow: 'follow',
			follower: 'Rage.Gift Follower',
			check: 'Check',
			notSubscribed:
				'You must subscribe to the Rage.Gift channel to claim rain.',
			notChatMember: 'You must join the Rage.Gift chat to claim rain.'
		},
		ru: {
			title: 'еще один шаг...',
			desc: 'Пожалуйста, убедись, что твой аккаунт соответствует требованиям для вступления в дождь',
			chatMember: 'участник чата',
			complete: 'выполнено',
			incomplete: 'не выполнено',
			join: 'вступить',
			follow: 'перейти',
			follower: 'Подписчик Rage.Gift',
			check: 'проверить',
			notSubscribed:
				'Ты должен подписаться на канал Rage.Gift, чтобы забрать дождь.',
			notChatMember: 'Ты должен вступить в чат Rage.Gift, чтобы забрать дождь.'
		}
	}
})

const emit = defineEmits<{
	(e: 'retry'): void
}>()

const model = defineModel<boolean>()

const close = () => {
	model.value = false
}

const toast = useToast()
const loading = ref(false)

const user = useUser()

const retry = async () => {
	if (loading.value) {
		return
	}

	emit('retry')

	await sleep(1500)

	loading.value = false
}

const subscribeLink = 'https://t.me/ragegift'
const joinLink = 'https://t.me/RageLounge'

const openLink = (href: string) => {
	WebApp.openTelegramLink(href)
}
</script>
